import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Monitor } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const HANDOVER_BADGE = {
  pending_approval: 'bg-purple-500/20 text-purple-400 border-purple-500/40',
  in_wallet: 'bg-amber-500/20 text-amber-400 border-amber-500/40',
  settled: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/40',
  none: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/40',
};
const HANDOVER_LABEL = { pending_approval: 'awaiting admin confirm', in_wallet: 'in staff wallet', settled: 'settled', rejected: 'handover rejected', none: 'no handover' };

export default function PosShiftsPanel({ formatCurrency }) {
  const [shifts, setShifts] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pos/shifts?limit=25`, auth());
      setShifts(r.data.shifts || []);
    } catch { setShifts(null); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener('pos-shifts-refresh', load);
    return () => window.removeEventListener('pos-shifts-refresh', load);
  }, [load]);

  if (!shifts || shifts.length === 0) return null;

  return (
    <div className="bg-wt-paper border border-wt-border rounded-xl p-6" data-testid="pos-shifts-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 rounded-lg bg-cyan-500/10"><Monitor className="h-6 w-6 text-cyan-400" /></div>
        <div>
          <h2 className="text-xl font-semibold text-wt-text-primary">POS Register Shifts</h2>
          <p className="text-wt-text-muted text-sm">Register cash flows into staff cash wallets on shift close — settle via Manager Cash Ledgers below</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-wt-text-muted border-b border-wt-border/50 text-left">
              <th className="py-2 pr-2">Store / Register</th><th className="pr-2">Staff</th>
              <th className="pr-2">Opened</th><th className="pr-2 text-right">Cash Sales</th>
              <th className="pr-2 text-right">Counted</th><th className="pr-2 text-right">Handover</th>
              <th className="pr-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {shifts.map((s) => (
              <tr key={s.id} className="border-b border-wt-border/30 text-wt-text-primary" data-testid={`pos-shift-row-${s.id}`}>
                <td className="py-2 pr-2">{s.store_name || '—'}<span className="text-wt-text-muted"> / {s.register_name || '—'}</span></td>
                <td className="pr-2">{s.opened_by_name}</td>
                <td className="pr-2 whitespace-nowrap text-wt-text-muted">{(s.opened_at || '').slice(0, 16).replace('T', ' ')}</td>
                <td className="pr-2 text-right">{s.status === 'open'
                  ? <span className="text-cyan-400">{formatCurrency(s.live_totals?.cash_sales || 0)} (live)</span>
                  : formatCurrency(s.cash_sales || 0)}</td>
                <td className="pr-2 text-right">{s.status === 'closed' ? formatCurrency(s.counted_cash || 0) : '—'}</td>
                <td className="pr-2 text-right">{s.status === 'closed' && s.handover_cash > 0 ? formatCurrency(s.handover_cash) : '—'}</td>
                <td className="pr-2">
                  {s.status === 'open'
                    ? <Badge className="text-[10px] bg-cyan-500/20 text-cyan-400 border-cyan-500/40">shift open</Badge>
                    : <Badge className={`text-[10px] ${HANDOVER_BADGE[s.handover_status] || HANDOVER_BADGE.none}`}>{HANDOVER_LABEL[s.handover_status] || s.handover_status}</Badge>}
                  {s.status === 'closed' && Math.abs(s.difference || 0) > 0.01 && (
                    <Badge className="text-[10px] ml-1 bg-red-500/20 text-red-400 border-red-500/40">diff {formatCurrency(s.difference)}</Badge>
                  )}
                  {s.status === 'closed' && s.card_difference != null && Math.abs(s.card_difference) > 0.01 && (
                    <Badge className="text-[10px] ml-1 bg-red-500/20 text-red-400 border-red-500/40">card batch {formatCurrency(s.card_difference)}</Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
